import * as React from "react";
import { Pressable, Text, View } from "react-native";
import Animated, { FadeIn } from "react-native-reanimated";
import { SafeAreaView } from "react-native-safe-area-context";
import * as DocumentPicker from "expo-document-picker";
import { Image } from "expo-image";
import * as ImagePicker from "expo-image-picker";
import {
  Link,
  Slot,
  Stack,
  usePathname,
  useRouter,
  useSegments,
} from "expo-router";
import { Feather } from "@expo/vector-icons";
import { debounce } from "lodash";
import { observer } from "mobx-react-lite";

import { getImageSize } from "~/utils/getImageSize";
import { AccountActionsProvider } from "~/components/AccountActions";
import { useAccountActions } from "~/components/AccountActions/context";
import { AccountSwitcher } from "~/components/AccountSwitcher";
import { FeedbackForm } from "~/components/FeedbackForm";
import {
  ResponsiveRenderer,
  useMediaType,
} from "~/components/ResponsiveRenderer";
import { UserDropdown } from "~/components/UserDropdown";
import { backlogSlug } from "~/app/(app)/(authenticated)/[account]/(account)/backlog";
import Logo from "~/assets/logo.svg";
import { useCurrentAccount } from "~/hooks/useCurrentAccount";
import { useCurrentSpace } from "~/hooks/useCurrentSpace";
import { useGetObjectAccessToken } from "~/hooks/useGetObjectAccessToken";
import { useGetR2AssetURL } from "~/hooks/useGetR2AssetURL";
import { icons, useHackyParams } from "~/lib/constants";
import { AccountStoreProvider, useAccountStore } from "~/stores/space";
import { Button } from "~/ui/Button";
import { Dropdown } from "~/ui/Dropdown";
import { TextInput } from "~/ui/TextInput";

export const unstable_settings = {
  initialRouteName: "(account)",
};

export default function AccountLayout() {
  const segments = useSegments();

  let accountId: string;
  let accountSlug: string;
  try {
    // rules-of-hooks is having a false-positive here :shrug:
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { id, slug } = useCurrentAccount();
    accountId = id;
    accountSlug = slug;
  } catch (e) {
    return (
      <View className="items-center justify-center">
        <Text className="text-text-primary text-2xl">
          Account not found or not accessible.
        </Text>
        <Link href="/">
          <Text className="text-text-primary">Return home</Text>
        </Link>
      </View>
    );
  }

  return (
    <AccountStoreProvider accountId={accountId}>
      <AccountLoadingScreen>
        <AccountActionsProvider>
          <ResponsiveRenderer
            webDesktop={
              <View className="grid max-h-screen min-h-screen grow grid-rows-[auto_1fr] overflow-hidden">
                <View className="border-divider-strong flex-row border-b px-6 py-4">
                  <View className="flex-row items-center gap-3">
                    <Link href={`/${accountSlug}`}>
                      <Logo className="size-6" />
                    </Link>
                    <AccountSwitcher />
                    {/* .find so that TS doesn't scream. .includes does work */}
                    {segments.find((s) => s === "[space]") && (
                      // TODO: should we hide this when opening backlog?
                      // space !== backlogSlug &&
                      <>
                        <View className="border-r-divider-strong self-stretch border-r" />
                        <SpaceSwitcher />
                      </>
                    )}
                  </View>

                  <View className="ml-auto flex-row items-center gap-2">
                    {segments.find((s) => s === "[space]") && (
                      <>
                        <SearchInput />
                        <View className="border-r-divider-strong self-stretch border-r" />
                      </>
                    )}
                    <AddNewMenu />
                    <Link
                      href={{
                        pathname:
                          "/(app)/(authenticated)/[account]/(space)/[space]/saves",
                        params: {
                          space: backlogSlug,
                          account: accountSlug,
                        },
                      }}
                      asChild
                    >
                      <Button
                        variant="outline"
                        size="small"
                        iconAfter={
                          <Feather
                            size={18}
                            name={icons.backlog.web}
                            className="text-icon"
                          />
                        }
                      />
                    </Link>
                    <Button
                      variant="outline"
                      size="small"
                      iconAfter={
                        <Feather
                          size={18}
                          name={icons.notification.web}
                          className="text-icon"
                        />
                      }
                    />
                    <FeedbackForm />
                    <UserDropdown />
                  </View>
                </View>
                {/* <UploadTest /> */}
                <Slot />
              </View>
            }
            default={
              <Stack>
                <Stack.Screen
                  name="collection"
                  options={{
                    presentation: "formSheet",
                    headerShown: false,
                    sheetAllowedDetents: "fitToContents",
                    sheetGrabberVisible: true,
                  }}
                />
                <Stack.Screen
                  name="link"
                  options={{
                    presentation: "formSheet",
                    headerShown: false,
                    sheetAllowedDetents: "fitToContents",
                    sheetGrabberVisible: true,
                  }}
                />
                <Stack.Screen
                  name="moveTo"
                  options={{
                    presentation: "formSheet",
                    headerShown: true,
                    sheetAllowedDetents: "fitToContents",
                    sheetGrabberVisible: true,
                  }}
                />
                <Stack.Screen
                  name="copyTo"
                  options={{
                    presentation: "formSheet",
                    headerShown: true,
                    sheetAllowedDetents: "fitToContents",
                    sheetGrabberVisible: true,
                  }}
                />
                <Stack.Screen
                  name="spaceForm"
                  options={{
                    presentation: "formSheet",
                    headerShown: true,
                    sheetAllowedDetents: "fitToContents",
                    sheetGrabberVisible: true,
                  }}
                />
                <Stack.Screen name="(account)" />
              </Stack>
            }
          />
        </AccountActionsProvider>
      </AccountLoadingScreen>
    </AccountStoreProvider>
  );
}

function AccountLoadingScreen(props: { children: React.ReactNode }) {
  const { isReady } = useAccountStore(true);

  const { isLoading } = useGetObjectAccessToken({ enabled: isReady });

  if (isLoading || !isReady) {
    return <LoadingScreen />;
  }

  return <>{props.children}</>;
}

function LoadingScreen() {
  return (
    <SafeAreaView className="h-screen items-center justify-center">
      <Animated.View entering={FadeIn.delay(150)} className="items-center">
        <Logo className="mx-auto my-2 size-10" />
        <Text className="text-text-primary text-28 font-semibold">
          Loading account...
        </Text>
      </Animated.View>
    </SafeAreaView>
  );
}

export function AddNewMenu() {
  const accountActions = useAccountActions();
  const { store: account } = useAccountStore();

  const params = useHackyParams<{ space?: string }>();
  const space = params.space ? account.spaceBySlug(params.space) : null;
  const { isDesktop } = useMediaType();

  return (
    <>
      <Dropdown
        asChild
        trigger={
          isDesktop ? (
            <Button
              size="small"
              role="button"
              variant="primary"
              className="gap-1 px-4"
              iconBefore={<Feather name={icons.create.web} size={20} />}
            >
              Add new
            </Button>
          ) : (
            <Button
              size="small"
              className="size-8 p-1"
              iconAfter={<Feather name="plus" size={24} />}
            />
          )
        }
        groups={[
          {
            items: [
              // {
              //   key: "note",
              //   title: "Note",
              //   icon: {
              //     web: "edit",
              //     ios: "pencil",
              //   },
              // },
              {
                key: "link",
                title: "Link",
                icon: icons.link,
                onSelect() {
                  accountActions.openAddSave({
                    spaceId: space?.id,

                    type: "URL",
                  });
                },
              },

              {
                key: "note",
                title: "Note",
                icon: icons.note,
                onSelect() {
                  accountActions.openAddSave({
                    spaceId: space?.id,
                    type: "NOTE",
                  });
                },
              },

              {
                key: "media",
                title: "Media",
                icon: icons.media,
                async onSelect() {
                  try {
                    // TODO: Doesn't media mean audio, video _and_ image?
                    await ImagePicker.requestMediaLibraryPermissionsAsync();
                    const result = await ImagePicker.launchImageLibraryAsync({
                      mediaTypes: ImagePicker.MediaTypeOptions.All,
                      presentationStyle:
                        ImagePicker.UIImagePickerPresentationStyle.FORM_SHEET,
                      base64: true,
                    });

                    if (!result.canceled) {
                      const asset = result.assets[0];

                      accountActions.openAddSave({
                        spaceId: space?.id,
                        type: "MEDIA",
                        mediaMeta: {
                          mimeType: asset.mimeType ?? "",
                          assetId: asset.assetId ?? "",
                          type:
                            asset.type ??
                            (asset.mimeType?.includes("video")
                              ? "video"
                              : "image"),
                          duration: asset.duration ?? 0,
                          fileName: asset.fileName ?? "",
                          fileSize: asset.fileSize ?? getImageSize(asset.uri),
                          height: asset.height,
                          width: asset.width,
                          uri: asset.uri,
                        },
                      });
                    }
                  } catch (e) {
                    console.error(e);
                  }
                },
              },
              {
                key: "file",
                title: "File",
                icon: icons.file,

                async onSelect() {
                  try {
                    const result = await DocumentPicker.getDocumentAsync({
                      type: ["application/*", "text/*"],
                    });

                    if (!result.canceled) {
                      const asset = result.assets[0];

                      accountActions.openAddSave({
                        spaceId: space?.id,

                        type: "FILE",
                        fileMeta: {
                          name: asset?.name ?? "",
                          uri: asset?.uri ?? "",
                          mimeType: asset?.mimeType ?? "",
                          size: asset?.size ?? 0,
                        },
                      });
                    }
                  } catch (e) {
                    console.error(e);
                  }
                },
              },
            ],
          },

          {
            title: "collection",
            items: [
              {
                key: "collection",
                title: "Collection",
                icon: icons.collection,
                onSelect() {
                  accountActions.openAddCollection();
                },
              },
            ],
          },

          {
            title: "workspace",
            items: [
              {
                key: "workspace",
                title: "Workspace",
                icon: icons.workspace,
                onSelect: () => {
                  accountActions.openAddSpace();
                },
              },
            ],
          },
        ]}
      />
    </>
  );
}

function SpaceSwitcher_(_props: any) {
  const { store } = useAccountStore();
  const account = useCurrentAccount();
  const { push } = useRouter();
  const { space: currentSpace } = useCurrentSpace();
  const { getR2AssetURL } = useGetR2AssetURL();
  return (
    <Dropdown
      asChild
      trigger={
        <Pressable role="button" className="flex-row items-center gap-2">
          <View className="bg-grey-dark-11 h-9 w-9 items-center justify-center overflow-hidden rounded-full border">
            <Image
              source={
                currentSpace.r2Key
                  ? getR2AssetURL(currentSpace.r2Key)
                  : undefined
              }
              placeholder={{
                uri: "thumbhash:/1QcSHQRnh493V4dIh4eXh1h4kJUI",
              }}
              contentFit="cover"
              style={{
                height: "100%",
                width: "100%",
              }}
              alt={currentSpace.displayName ?? ""}
            />
          </View>
          <Text className="text-text-primary">{currentSpace.displayName}</Text>
          <Feather name="code" className="text-text-secondary rotate-90" />
        </Pressable>
      }
      groups={[
        {
          title: "spaces",
          items:
            store.spacesAsArrayWithoutBacklog.map((space) => ({
              key: space.id,
              title: space.displayName ?? space.slug ?? "-",
              onSelect: () => {
                if (currentSpace.id === space.id) return;

                push({
                  pathname:
                    "/(app)/(authenticated)/[account]/(space)/[space]/saves",
                  params: {
                    account: account.slug,
                    space: space.slug,
                  },
                });
              },
            })) ?? [],
        },
      ]}
    />
  );
}

const SpaceSwitcher = observer(SpaceSwitcher_);

function SearchInput() {
  const params = useHackyParams<{ search?: string }>();

  const pathname = usePathname();
  const { replace } = useRouter();
  const [value, setValue] = React.useState(params.search ?? "");
  const debouncedReplace = React.useCallback(debounce(replace, 250), [replace]);

  React.useEffect(() => {
    setValue(params.search ?? "");
  }, [params.search]);

  return (
    <TextInput
      placeholder="Search"
      size="small"
      value={value}
      onChangeText={(v) => {
        setValue(v);
        debouncedReplace({
          pathname,

          params: {
            ...params,
            search: v,
          },
        });
      }}
    />
  );
}
